// ============================================================================
// T-ALE-15 · LA CAMPANITA (R-ALE-02).
//
// Lo pidió Cristhian el 31-ago-2026 viendo dos carteles rojos pegados en su
// panel: *«debe haber una manera de que uno pueda solucionarlos o quitarlos para
// que no estén ahí siempre estorbando […] quizás deberíamos crear una campanita
// de notificaciones»*.
//
// Lleva **todo lo abierto** —graves y avisos—, cada uno con su «ya lo vi». La
// franja se queda arriba solo con los `grave`, que son los que tienen que
// estorbar.
//
// ⭐ CONSUME EL MISMO `EstadoAlertas` QUE LA FRANJA, y por eso «ya lo vi» las
//    apaga a las dos A LA VEZ y sin recargar. No consulta nada por su cuenta:
//    ni recibe `sb`.
//
// ⛔ «Ya lo vi» NO ES «cerrar». Si el incidente sigue ocurriendo, su siguiente
//    grito lo devuelve a la pantalla solo (lo garantiza `v_alertas_abiertas`,
//    migr 0044). Por eso el botón no dice «resolver» ni «descartar»: dice lo que
//    de verdad hace.
//
// ⛔ Y si el «ya lo vi» rebota, se DICE. Un botón que no hace nada y se calla es
//    peor que un botón que no está.
// ============================================================================
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Bell, Check, ShieldAlert } from "lucide-react";
import { desdeHace } from "./FranjaAlertas.tsx";
import type { EstadoAlertas } from "./useAlertas.ts";

export function Campanita({ estado }: { estado: EstadoAlertas }) {
  const { alertas, incompleta, fallo, esVictorix, marcarVista, marcando, falloAlMarcar } = estado;
  const [abierta, setAbierta] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);

  // Cerrar al pulsar fuera. Es una campanita, no una pantalla: si estorba, se
  // quita con un clic en cualquier sitio.
  useEffect(() => {
    if (!abierta) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierta(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierta]);

  const lista = alertas ?? [];
  const n = lista.length;

  return (
    <div className="relative" ref={caja}>
      <button type="button" onClick={() => setAbierta((v) => !v)}
        data-campanita aria-expanded={abierta}
        aria-label={fallo
          ? "Incidentes: no pude comprobar"
          : n === 0 ? "Incidentes: ninguno abierto" : `Incidentes abiertos: ${n}`}
        title="Incidentes"
        className="relative h-9 w-9 grid place-items-center rounded-lg text-tinta-2
          hover:text-tinta hover:bg-elevado active:scale-90 transition cursor-pointer">
        <Bell size={18} strokeWidth={1.75} />
        {n > 0 && (
          <span data-campanita-contador
            className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 grid place-items-center
              rounded-full bg-peligro text-white text-[10.5px] font-semibold leading-none">
            {n > 99 ? "99+" : n}
          </span>
        )}
      </button>

      {abierta && (
        <div data-campanita-panel role="dialog" aria-label="Incidentes abiertos"
          className="absolute right-0 top-full mt-1.5 w-[min(92vw,380px)] max-h-media-pantalla overflow-y-auto
            rounded-xl border border-borde bg-superficie shadow-xl z-50">
          <div className="px-3 py-2 border-b border-borde-suave text-[13px] font-medium">
            Incidentes abiertos
          </div>

          {/* ⛔ El fallo de la consulta se dice también aquí: la campanita sin
              renglones parecería «todo bien», que es justo lo que no se sabe. */}
          {fallo && (
            <p data-campanita-fallo className="px-3 py-3 text-[13px] text-tinta-2">
              No pude comprobar el estado del bot. Vuelvo a intentarlo en un minuto.
            </p>
          )}

          {falloAlMarcar && (
            <p role="alert" data-campanita-fallo-marcar
              className="px-3 py-2 text-[13px] text-peligro border-b border-borde-suave">
              {falloAlMarcar}
            </p>
          )}

          {!fallo && n === 0 && (
            <p className="px-3 py-3 text-[13px] text-tinta-2">
              Nada abierto. El bot está atendiendo.
            </p>
          )}

          <ul>
            {lista.map((a) => {
              const grave = a.severidad === "grave";
              const Icono = grave ? ShieldAlert : AlertTriangle;
              return (
                <li key={a.id} data-alerta-campanita={a.clave}
                  className="flex items-start gap-2 px-3 py-2.5 border-b border-borde-suave last:border-b-0">
                  <Icono size={15} strokeWidth={1.75} aria-hidden="true"
                    className={`shrink-0 mt-[3px] ${grave ? "text-peligro" : "text-alerta"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] leading-snug font-medium">{a.titulo}</p>
                    <p className="text-[11.5px] text-tinta-2">{desdeHace(a.creada_en)}</p>
                    {/* ⛔ SOLO VICTORIX, igual que en la franja: al dueño el
                        `detalle` ni se le pidió a la base. */}
                    {esVictorix && a.detalle && Object.keys(a.detalle).length > 0 && (
                      <p data-detalle-tecnico
                        className="mt-0.5 font-mono text-[11px] text-tinta-2 break-all">
                        {JSON.stringify(a.detalle)}
                      </p>
                    )}
                  </div>
                  <button type="button" data-ya-lo-vi={a.clave}
                    disabled={marcando === a.clave}
                    onClick={() => void marcarVista(a.clave)}
                    title="Si vuelve a pasar, reaparece solo"
                    aria-label={`Ya lo vi: ${a.titulo}`}
                    className="shrink-0 flex items-center gap-1 h-7 px-2 rounded-lg border border-borde
                      text-[12px] text-tinta-2 hover:text-tinta hover:bg-elevado
                      disabled:opacity-50 disabled:cursor-default active:scale-95 transition cursor-pointer">
                    <Check size={13} strokeWidth={2} aria-hidden="true" />
                    Ya lo vi
                  </button>
                </li>
              );
            })}
          </ul>

          {incompleta && (
            <p data-campanita-incompleta className="px-3 py-2 text-[12.5px] text-tinta-2
              border-t border-borde-suave">
              Hay más incidentes abiertos de los que caben aquí: estos no son todos.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
